import React, { type ButtonHTMLAttributes, forwardRef, type MouseEvent } from "react";
import { type ActionProps, SIZE, VARIANT } from "@purpur/action";
import type { BaseProps } from "@purpur/common-types";
import { Spinner } from "@purpur/spinner";
import c from "classnames/bind";

import styles from "./button.module.scss";

export const BUTTON_VARIANT = {
  ...VARIANT,
  DESTRUCTIVE: "destructive",
  TERTIARY_PURPLE: "tertiary-purple",
  TEXT: "text",
} as const;

export const buttonVariants = Object.values(BUTTON_VARIANT);
export type ButtonVariant = (typeof BUTTON_VARIANT)[keyof typeof BUTTON_VARIANT];

const cx = c.bind(styles);
const rootClassName = "purpur-button";

type ButtonType = Pick<ButtonHTMLAttributes<HTMLButtonElement>, "type">["type"];
export const BUTTON_TYPES: ButtonType[] = ["button", "submit", "reset"] as const;

type ButtonBaseProps = BaseProps<"button"> & { ai?: boolean; loading?: boolean };

export type ButtonProps =
  | (ActionProps<ButtonVariant> & ButtonBaseProps & { iconOnly?: false | undefined })
  | (ActionProps<Exclude<ButtonVariant, "destructive">> & ButtonBaseProps & { iconOnly: true });

type DistributiveOmit<T, K extends keyof never> = T extends unknown ? Omit<T, K> : never;

const shouldRenderChildren = ({ iconOnly, loading }: Pick<ButtonProps, "iconOnly" | "loading">) =>
  !(iconOnly && loading);

export const Button = forwardRef<
  HTMLButtonElement,
  DistributiveOmit<ButtonProps, "type"> & { type?: ButtonType }
>(
  (
    {
      ai = false,
      children,
      className,
      disabled = false,
      fullWidth = false,
      iconOnly,
      loading = false,
      negative = false,
      onClick,
      size = "md",
      variant,
      type = "button",
      ...props
    },
    ref
  ) => {
    if (iconOnly && variant === ("destructive" as string)) {
      return null;
    }

    const variantClass = `${variant}${ai ? "-ai" : ""}${negative ? "-negative" : ""}`;

    const classes = cx(
      [rootClassName, `${rootClassName}--${variantClass}`, `${rootClassName}--${size}`],
      {
        [`${rootClassName}--icon-only`]: iconOnly,
        [`${rootClassName}--negative`]: negative,
        [`${rootClassName}--full-width`]: fullWidth,
        [`${rootClassName}--disabled`]: disabled || loading,
      },
      className
    );

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (disabled || loading) {
        e.preventDefault();
      } else if (onClick) {
        onClick(e);
      }
    };

    const _disabled = Boolean(props["aria-disabled"]) || disabled || loading;
    const renderChildren = shouldRenderChildren({ iconOnly, loading });

    return (
      <button
        className={classes}
        type={type}
        ref={ref}
        aria-disabled={_disabled}
        onClick={handleClick}
        {...props}
      >
        {loading && (
          <Spinner
            size={size === SIZE.LG ? "xs" : "xxs"}
            negative={negative}
            disabled={_disabled}
            className={cx(`${rootClassName}__spinner`)}
          />
        )}
        {renderChildren ? children : null}
      </button>
    );
  }
);

Button.displayName = "Button";
